import filter from "lodash/filter";
import isNil from "lodash/isNil";
import { Button } from "PFComponents/button";
import { Inline } from "PFComponents/containers";
import { ReadOnlyField } from "PFComponents/read_only_field";
import { Typography } from "PFComponents/typography";
import { isRankable } from "PFCore/helpers/custom_type";
import { useCurrentAccount } from "PFCore/hooks/queries/account";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Experience, ProfileCustomValue, PureCustomValueCamelCased } from "PFTypes";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { useCustomValuePreviewContext } from "../../context/custom_value_preview_context";
import css from "../../custom_value_preview.module.scss";
import { AttributeCheckbox } from "../../parts/attribute_checkbox";
import { CertificatesList } from "../../parts/certificates_list";
import { Details } from "../../parts/details";
import { ExperienceToggle } from "../../parts/experience_toggle";
import { LearningPlatformLink } from "../../parts/learning_platform_link";
import { ParentChildSection } from "../../parts/parent_child_section/parent_child_section";
import { SourceData } from "../../parts/source_data";
import { VerifiedIcons } from "../../parts/verified_icons";
import { getInfoMessage } from "./custom_value_content.utils";

type ProfileCustomValueContentProps = {
  experience: Experience | null;
  isCore: boolean;
  isDevelopmental: boolean;
  customValue: PureCustomValueCamelCased;
  profileConnection?: ProfileCustomValue;
  allowEdit: boolean;
  hasAnyFormField: boolean;
  onExperienceChange: (experience: Experience) => void;
  onCoreChange: (isCore: boolean) => void;
  onDevelopmentalChange: (isDevelopmental: boolean) => void;
  onResetForm: VoidFunction;
};

export const ProfileCustomValueContent = ({
  experience,
  isCore,
  isDevelopmental,
  allowEdit,
  hasAnyFormField,
  customValue,
  profileConnection,
  onExperienceChange,
  onCoreChange,
  onDevelopmentalChange,
  onResetForm
}: ProfileCustomValueContentProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "customValueSidepanel" });
  const { t: tTranslation } = useTranslation();
  const { data: currentProfile } = useCurrentProfile();
  const { data: currentAccount } = useCurrentAccount();
  const { formatDate } = useDateFormatter();
  const { isNew, customType, allowSearch, profileId, parentChildProfileValues } =
    useCustomValuePreviewContext();

  const {
    config: {
      profile: { developmental_skills_limit: developmentalSkillsLimit, top_skills_limit: coreSkillsLimit }
    }
  } = currentAccount;
  const rankable = isRankable(customType);
  const isSkills = customType.name === "skills";
  const isMe = profileId === currentProfile.id;
  const coreSkillsCount = filter(currentProfile.skills, "top").length;
  const developmentalSkillsCount = filter(currentProfile.skills, "developmental").length;
  const showLearningPlatformLink = isSkills && isNew && isMe;
  const infoMessage = getInfoMessage({
    isNew,
    allowEdit,
    hasAnyFormField,
    messages: {
      new: {
        allowed: t("youDontHaveValueAddIt"),
        notAllowed: t("youDontHavePermissionsToAdd")
      },
      edit: {
        allowed: t("youAlreadyHaveValueUpdateIt")
      }
    }
  });
  const CoreDevelopmentalWrapper = allowEdit ? Fragment : Inline;
  const showCoreDevelopmental = isSkills && (allowEdit || isCore || isDevelopmental);
  const hasParentChildInfo = !isNil(parentChildProfileValues);

  return (
    <>
      {isNew && allowSearch && (
        <Button
          kind="text"
          text={t("searchForAnotherCustomValue")}
          icon="arrow-left"
          onClick={onResetForm}
          className={css.linkButton}
        />
      )}
      {infoMessage && <Typography variant="bodyRegular">{infoMessage}</Typography>}
      {rankable && (
        <ExperienceToggle
          experience={experience}
          onChange={onExperienceChange}
          canEdit={allowEdit}
          isRequirement={false}
        />
      )}
      {showCoreDevelopmental && (
        <CoreDevelopmentalWrapper>
          <AttributeCheckbox
            checked={isCore}
            onChange={onCoreChange}
            label={t("core")}
            iconName="core"
            disabled={!profileConnection?.top && coreSkillsCount >= coreSkillsLimit}
            disabledTooltip={tTranslation("coreSkillsLimit", {
              limit: coreSkillsLimit
            })}
            canEdit={allowEdit}
            classes={{ readonly: css.halfWidth }}
          />
          <AttributeCheckbox
            checked={isDevelopmental}
            onChange={onDevelopmentalChange}
            label={t("developmental")}
            iconName="development"
            disabled={
              !profileConnection?.developmental && developmentalSkillsCount >= developmentalSkillsLimit
            }
            disabledTooltip={tTranslation("developmentalSkillsLimit", {
              limit: developmentalSkillsLimit
            })}
            canEdit={allowEdit}
            classes={{ readonly: css.halfWidth }}
          />
        </CoreDevelopmentalWrapper>
      )}
      {hasParentChildInfo && <ParentChildSection />}
      {profileConnection && (
        <>
          <Inline spread>
            <VerifiedIcons profileConnection={profileConnection} />
            <SourceData source={profileConnection?.source ?? null} />
          </Inline>
          <Inline spread>
            <ReadOnlyField
              classes={{ root: css.halfWidth }}
              label={t("addedToProfile")}
              value={formatDate(profileConnection?.createdAt)}
            />
            <ReadOnlyField
              classes={{ root: css.halfWidth }}
              label={t("lastEditedInProfile")}
              value={formatDate(profileConnection?.updatedAt)}
            />
          </Inline>
        </>
      )}
      {showLearningPlatformLink && <LearningPlatformLink skillName={String(customValue.text)} />}
      <Details customValue={customValue} />
      <CertificatesList />
    </>
  );
};
